// Judging a lab value against its field's reference range, in the browser.
//
// Kept in step with backend/utils/labResultFlags.js on purpose: the form
// colours a box red the moment the doctor types a value outside the range, and
// the server decides what is actually stored. The two must agree, or a value
// the form showed as normal is saved as high. Any change to the rules goes in
// both files.
//
// Also here: turning the visit form's lab cards into what the API accepts, and
// back, and picking the right language for a catalog name.

export const FLAGS = ['low', 'normal', 'high', 'abnormal'];

export const isBlank = (value) =>
    value === null || typeof value === 'undefined' ||
    (typeof value === 'string' && value.trim() === '');

const isFiniteNumber = (value) => typeof value === 'number' && Number.isFinite(value);

const sameText = (a, b) =>
    String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase();

// The numeric range that applies to this patient: the sex-specific one when
// the field has one for the patient's sex, otherwise the general adult range.
export const rangeFor = (field, sex) => {
    const bySex = field && field.sexRanges && sex ? field.sexRanges[sex] : null;
    if (bySex && (isFiniteNumber(bySex.low) || isFiniteNumber(bySex.high))) {
        return {
            low: isFiniteNumber(bySex.low) ? bySex.low : null,
            high: isFiniteNumber(bySex.high) ? bySex.high : null
        };
    }
    return {
        low: field && isFiniteNumber(field.referenceRangeLow) ? field.referenceRangeLow : null,
        high: field && isFiniteNumber(field.referenceRangeHigh) ? field.referenceRangeHigh : null
    };
};

// The range as printed beside the box: "12 – 15.5", "≤ 4", "≥ 90", or the
// reference text of a non-numeric field. '' when there is nothing to say.
export const rangeLabel = (field, sex) => {
    if (!field) return '';
    if (field.dataType === 'number') {
        const { low, high } = rangeFor(field, sex);
        if (low !== null && high !== null) return `${low} – ${high}`;
        if (low !== null) return `≥ ${low}`;
        if (high !== null) return `≤ ${high}`;
        return '';
    }
    return field.referenceText || '';
};

// low | normal | high | abnormal | null (nothing to judge).
export const flagFor = (field, value, sex) => {
    if (!field || isBlank(value)) return null;

    switch (field.dataType) {
        case 'number': {
            const number = typeof value === 'number' ? value : Number(value);
            if (!Number.isFinite(number)) return null;
            const { low, high } = rangeFor(field, sex);
            if (low !== null && number < low) return 'low';
            if (high !== null && number > high) return 'high';
            if (low === null && high === null) return null;
            return 'normal';
        }
        case 'boolean': {
            const normalIsPositive = sameText(field.referenceText, 'positive');
            const positive = value === true || sameText(value, 'positive') || sameText(value, 'true');
            return positive === normalIsPositive ? 'normal' : 'abnormal';
        }
        case 'select': {
            if (isBlank(field.referenceText)) return null;
            return sameText(value, field.referenceText) ? 'normal' : 'abnormal';
        }
        case 'text':
        default: {
            if (isBlank(field.referenceText)) return null;
            return sameText(value, field.referenceText) ? 'normal' : null;
        }
    }
};

// The name of a test or field in the current language, falling back to the
// English name. `entity` is a LabTest, a LabTestField, or a result snapshot.
export const localizedName = (entity, lang) => {
    if (!entity) return '';
    const base = entity.name || entity.label || '';
    const translated = entity.translations && entity.translations[lang];
    return translated && translated.trim() ? translated : base;
};

// What is shown for a stored value: a boolean as Positive/Negative (through the
// translator), a number as itself, text as itself, an empty box as '—'.
export const displayValue = (value, dataType, t = (k, fallback) => fallback) => {
    if (isBlank(value)) return '—';
    if (dataType === 'boolean' || typeof value === 'boolean') {
        return value === true || sameText(value, 'positive')
            ? t('labPositive', 'Positive')
            : t('labNegative', 'Negative');
    }
    return String(value);
};

const idOf = (value) => (value && value._id ? value._id : value);
const idString = (value) => (isBlank(value) ? '' : String(idOf(value)));

// Visit form -> API.
//
// A form card is { _id?, labTestId, results: { [fieldId]: value }, note }; the
// API wants { _id?, labTestId, results: [{ labTestFieldId, value }], note }.
// Every field of the test is sent, an empty box as null, so the server stores
// the full shape of the test and never an ''.
export const toVisitLabTestPayload = (cards = [], catalog = []) => {
    const byId = new Map(catalog.map((test) => [idString(test._id), test]));
    return (cards || [])
        .filter((card) => card && card.labTestId)
        .map((card) => {
            const test = byId.get(idString(card.labTestId));
            const values = card.results || {};
            const fields = test ? test.fields : Object.keys(values).map((key) => ({ _id: key }));
            return {
                ...(card._id ? { _id: card._id } : {}),
                labTestId: idString(card.labTestId),
                note: typeof card.note === 'string' ? card.note : '',
                results: fields.map((field) => {
                    const raw = values[idString(field._id)];
                    return {
                        labTestFieldId: idString(field._id),
                        value: isBlank(raw) ? null : raw
                    };
                })
            };
        });
};

// API -> visit form, for the edit form. The stored rows already carry every
// field; null values come back as undefined so the boxes render empty.
export const fromVisitLabTests = (rows = []) => (rows || []).map((row) => ({
    _id: row._id,
    labTestId: idString(row.labTestId),
    note: row.note || '',
    results: (row.results || []).reduce((acc, result) => {
        const value = result.value;
        acc[idString(result.labTestFieldId)] = value === null ? undefined : value;
        return acc;
    }, {})
}));
